import type { ConfirmSubscriptionPayload, CreateSubscriptionResponse } from "../api/types";

const CHECKOUT_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

type RazorpaySuccessResponse = {
	razorpay_payment_id: string;
	razorpay_subscription_id: string;
	razorpay_signature: string;
};

type RazorpayCheckout = {
	open: () => void;
};

declare global {
	interface Window {
		Razorpay?: new (options: Record<string, unknown>) => RazorpayCheckout;
	}
}

/**
 * Copy the checkout shows the user, supplied by the caller. Razorpay renders `name` and
 * `description` in its own modal, and the rejection messages reach the page's error banner, so all
 * four are translated strings rather than literals in this module.
 */
export type RazorpayCheckoutLabels = {
	name: string;
	description: string;
	scriptError: string;
	unavailable: string;
	dismissed: string;
};

let scriptPromise: Promise<void> | null = null;

/** Loads checkout.js once and caches the promise so repeated opens don't re-inject the script. */
const loadCheckoutScript = (scriptError: string): Promise<void> => {
	if (window.Razorpay) {
		return Promise.resolve();
	}

	if (!scriptPromise) {
		scriptPromise = new Promise<void>((resolve: any, reject: any) => {
			const script = document.createElement("script");
			script.src = CHECKOUT_SCRIPT_URL;
			script.async = true;
			script.onload = () => resolve();
			script.onerror = () => {
				scriptPromise = null;
				reject(new Error(scriptError));
			};
			document.body.appendChild(script);
		});
	}

	return scriptPromise;
};

/**
 * Opens the Razorpay checkout for a subscription created via the subscriptions API.
 * Resolves with the values the confirm endpoint needs to verify the payment; rejects when the
 * user dismisses the checkout without paying.
 */
export const openRazorpayCheckout = (
	subscription: CreateSubscriptionResponse,
	labels: RazorpayCheckoutLabels
): Promise<ConfirmSubscriptionPayload> =>
	loadCheckoutScript(labels.scriptError).then(
		() =>
			new Promise<ConfirmSubscriptionPayload>((resolve: any, reject: any) => {
				if (!window.Razorpay) {
					reject(new Error(labels.unavailable));

					return;
				}

				const checkout = new window.Razorpay({
					key: subscription.razorpayKey,
					subscription_id: subscription.subscriptionId,
					name: labels.name,
					description: labels.description,
					prefill: {
						name: subscription.customerName ?? undefined,
						email: subscription.customerEmail ?? undefined
					},
					handler: (response: RazorpaySuccessResponse) => {
						resolve({
							razorpayPaymentId: response.razorpay_payment_id,
							razorpaySubscriptionId: response.razorpay_subscription_id,
							razorpaySignature: response.razorpay_signature
						});
					},
					modal: {
						ondismiss: () => reject(new Error(labels.dismissed))
					}
				});

				checkout.open();
			})
	);
